/**
 * STUDIO domain state — the drawer and its tool forms.
 *
 * SIMULATED. Every Studio action is a 1300ms timer that appends a version record;
 * nothing processes audio. Split Stems produces no stems, cover art produces no
 * image (only a deterministic gradient seeded from `artworkSeed`).
 *
 * The four action prompts
 * -----------------------
 * `extendPrompt`, `remixPrompt`, `replacePrompt` and `coverPrompt` are collected
 * by the UI but not consumed by the simulation, because there is nothing to send
 * them to. They are retained deliberately and are now threaded through
 * `buildAction()` into the typed StudioAction that generationApi receives, so
 * Phase 5 forwards real prompt text by changing one function body rather than
 * re-plumbing five forms.
 */
import { create } from 'zustand';
import { REMIX_STYLES } from '../data/music';
import type { AudioContainer } from '../lib/audioUpload';
import type { CoverSource, StudioAction, StudioActionKind, StudioDrawer } from '../types/domain';

/**
 * Artwork produced but not yet committed to the song.
 *
 * Carries the same two fields the song does, so applying it is a plain patch rather
 * than a translation, plus where it came from — an uploaded image and a generated
 * one are both valid cover art and the caption says which.
 */
export interface PendingCover {
  source: CoverSource;
  /** The image, when there is one. Null on the generated path. */
  coverUrl: string | null;
  /** Gradient seed, on the generated path. */
  artworkSeed?: string;
  /** Style the generation used, carried through to the song on apply. */
  coverStyle?: string;
  /** Original filename, shown so the user can confirm they picked the right file. */
  filename?: string;
}

interface StudioState {
  /** Song open in the Studio workspace, or null when closed. */
  songId: string | null;
  /**
   * The last song the Studio was opened on. Survives closing it.
   *
   * `songId` is synced to the route param and cleared when the workspace unmounts,
   * which makes it the wrong thing to ask "which track was I working on?". The
   * Studio nav row needs exactly that: /studio resolves to a song, and resuming
   * the track you were last on is the only answer that is not arbitrary. See
   * routes/StudioEntry.tsx.
   *
   * Not persisted across reloads — nothing else in this app is either, and a stale
   * id from a previous session would have to be re-validated against a library
   * that is itself rebuilt from fixtures on load.
   */
  lastSongId: string | null;
  /** True when leaving an open workspace should return to the last Studio view. */
  resumeStudio: boolean;
  /** Which tool panel is open. null = the song overview. */
  drawer: StudioDrawer;
  /** True during the simulated processing delay. */
  processing: boolean;
  /** Songs whose separated stem files are ready to download in this session. */
  readyStemsSongIds: string[];
  /**
   * Version ids selected for comparison, in selection order. 0, 1 or 2 entries —
   * the one-entry state is the "pick a second version" step, so this cannot be a
   * fixed pair.
   */
  compareIds: string[];

  // Tool form values. The four *Prompt fields are the preserved prompts.
  extendFromText: string | null;
  extendSecondsText: string;
  extendTemperatureText: string;
  extendVarianceText: string;
  extendPrompt: string;
  remixStyle: string | null;
  remixPrompt: string;
  remixLyricsOverride: string;
  replaceFrom: string;
  replaceTo: string;
  replaceDuration: string;
  replaceSteps: string;
  replacePrompt: string;
  stemsShifts: number;
  coverStyle: string | null;
  coverPrompt: string;

  /*
    EDIT (Trim/Cut/Split) panel state. Lives here rather than as component-local state,
    for the same reason the other tool forms do: TrimPanel only renders while `drawer ===
    'trim'`, so a plain `useState` resets to its defaults every time the drawer switches
    away and back. null on the *Text fields means "untouched for this song" — the panel
    still computes its usual duration-derived default (0:00, the whole track, its
    midpoint) the first time, and only this store remembers a value across remounts.
    See components/studio/TrimPanel.tsx and setEditForm below.
  */
  editActiveSection: EditSection;
  editStartText: string | null;
  editEndText: string | null;
  editSplitAtText: string | null;
  editCrossfadeSection: EditCrossfadeSection;
  editCrossfadeSecondsText: string;
  editCutExecuted: boolean;
  editSplitParts: { firstSongId: string; secondSongId: string } | null;
  editCrossfadeFirstVersionId: string | null;
  editCrossfadeSecondVersionId: string | null;
  editFadeSecondsText: string;

  /*
    UPLOAD panel state. Read and written LIVE (`useStudioStore(s => s.uploadFile)`, not a
    one-time lazy `useState` initializer) rather than the snapshot-on-unmount pattern the
    Edit fields above use, and that difference is deliberate: the upload request itself
    outlives the component (a plain async function call, not tied to any effect), so if
    the user leaves Upload mid-request and comes back, the NEW panel instance must see
    the SAME in-flight state the old one started — including while it is still running,
    not just whatever was true at the moment of leaving. `uploadFile` is the actual `File`
    object — safe to hold here because this store is a plain in-memory Zustand store with
    no persist middleware (see `lastSongId`'s comment above), so nothing ever tries to
    serialize it. See components/studio/UploadPanel.tsx, setUploadForm and
    setUploadSubmitting below.
  */
  uploadFile: File | null;
  uploadContainer: AudioContainer | null;
  uploadTitle: string;
  uploadArtistName: string;
  uploadGenre: string;
  uploadArtistGender: string;
  uploadUploaderName: string;
  /** True while the accepted file is in flight to the server. Survives leaving and
      returning to the Upload panel, for the reason above. */
  uploadSubmitting: boolean;

  /**
   * Artwork that has been produced but NOT yet applied to the song.
   *
   * The reason this exists: applying must be a deliberate act. Generating or
   * uploading an image used to write straight onto the song, so an experimental
   * generation silently replaced artwork the user was happy with and there was no
   * way back. Now the result lands here, the preview shows it, and only Apply
   * commits it. Null means the preview is showing the song's own artwork.
   */
  pendingCover: PendingCover | null;
  setPendingCover: (cover: PendingCover | null) => void;

  openStudio: (songId: string) => void;
  closeStudio: () => void;
  /**
   * Retargets the workspace onto a different song without touching the open drawer,
   * section or form state.
   *
   * Unlike `openStudio`, which is the entry point and resets everything a fresh song
   * ought to start clean with, this is for an edit that produces a NEW song while the
   * user is mid-panel — Split's Crossfade is the first caller. The drawer stays open on
   * whatever section it was, so the result can keep being edited without a re-open.
   */
  setSongId: (songId: string) => void;
  setDrawer: (drawer: StudioDrawer) => void;
  setProcessing: (processing: boolean) => void;
  markStemsReady: (songId: string) => void;
  clearReadyStems: (songId: string) => void;
  /**
   * Adds or removes a version from the comparison, capped at two.
   *
   * Selecting a third replaces the selection with just that version, matching the
   * prototype: it never held more than a pair.
   */
  toggleCompare: (versionId: string) => void;
  clearCompare: () => void;
  /** Drops a deleted version from the compare selection. */
  dropFromCompare: (versionId: string) => void;
  /** Updates one tool form field. */
  setField: <K extends StudioFormField>(field: K, value: StudioState[K]) => void;
  /**
   * Persists TrimPanel's current UI state in one write — called from its unmount
   * cleanup, not on every keystroke, so switching away from Edit and back resumes
   * exactly where it was left. See components/studio/TrimPanel.tsx.
   */
  setEditForm: (snapshot: EditFormSnapshot) => void;
  /** Clears the remembered Edit panel state so a newly loaded version starts from its own defaults. */
  resetEditForm: () => void;
  /**
   * Writes the chosen file, its detected container, and the title in one call — used
   * whenever the panel's selection changes (a file is picked or removed, or a submit
   * succeeds), not just on unmount. See components/studio/UploadPanel.tsx.
   */
  setUploadForm: (snapshot: UploadFormSnapshot) => void;
  /**
   * Marks the upload request in flight or finished.
   *
   * Set directly around the `await` in UploadPanel's submit — not local `useState` —
   * because the request is a plain async function call with no effect or subscription
   * tying it to the component: it keeps running if the user leaves the panel, and
   * without this living in the store the NEXT mount would have no way to know a request
   * was still out there and show the spinner again.
   */
  setUploadSubmitting: (submitting: boolean) => void;
  /**
   * Builds the typed action payload for the currently open song, carrying the
   * relevant prompt text. Returns null when no song is open.
   *
   * `regenerate` needs the lyric text, which lives in the editor rather than in this
   * store — so it is passed in. That is deliberate: keeping a second copy of the
   * lyrics here would create the question of which copy is current, and the answer
   * would eventually be "the wrong one".
   */
  buildAction: (
    kind: Exclude<
      StudioActionKind,
      'trim' | 'cut' | 'split' | 'crossfade' | 'insertSilence' | 'fade' | 'normalize' | 'master'
    >,
    lyrics?: string
  ) => StudioAction | null;
  reset: () => void;
}

/** The tool form fields `setField` accepts. */
export type StudioFormField = keyof Pick<
  StudioState,
  | 'extendFromText'
  | 'extendSecondsText'
  | 'extendTemperatureText'
  | 'extendVarianceText'
  | 'extendPrompt'
  | 'remixStyle'
  | 'remixPrompt'
  | 'remixLyricsOverride'
  | 'replaceFrom'
  | 'replaceTo'
  | 'replaceDuration'
  | 'replaceSteps'
  | 'replacePrompt'
  | 'stemsShifts'
  | 'coverStyle'
  | 'coverPrompt'
>;

/** Which Edit sub-section (Trim/Cut/Split/...) is open. See TrimPanel.tsx. */
export type EditSection = 'trim' | 'cut' | 'split' | 'insertSilence' | 'fade' | 'normalize';
export type EditCrossfadeSection = 'latest' | 'picker';

/** The full snapshot `setEditForm` writes in one call. See StudioState.editActiveSection etc. */
export interface EditFormSnapshot {
  editActiveSection: EditSection;
  editStartText: string;
  editEndText: string;
  editSplitAtText: string;
  editCrossfadeSection: EditCrossfadeSection;
  editCrossfadeSecondsText: string;
  editCutExecuted: boolean;
  editSplitParts: { firstSongId: string; secondSongId: string } | null;
  editCrossfadeFirstVersionId: string | null;
  editCrossfadeSecondVersionId: string | null;
  editFadeSecondsText: string;
}

/** The full snapshot `setUploadForm` writes in one call. See StudioState.uploadFile etc. */
export interface UploadFormSnapshot {
  uploadFile: File | null;
  uploadContainer: AudioContainer | null;
  uploadTitle: string;
  uploadArtistName: string;
  uploadGenre: string;
  uploadArtistGender: string;
  uploadUploaderName: string;
}

const INITIAL = {
  songId: null,
  lastSongId: null,
  resumeStudio: false,
  drawer: null as StudioDrawer,
  processing: false,
  readyStemsSongIds: [] as string[],
  compareIds: [] as string[],
  extendFromText: null as string | null,
  extendSecondsText: '15',
  extendTemperatureText: '1',
  extendVarianceText: '3',
  extendPrompt: '',
  remixStyle: null,
  remixPrompt: '',
  remixLyricsOverride: '',
  replaceFrom: '',
  replaceTo: '',
  replaceDuration: '',
  replaceSteps: '6',
  replacePrompt: '',
  stemsShifts: 1,
  coverStyle: null,
  coverPrompt: '',
  editActiveSection: 'trim' as EditSection,
  editStartText: null as string | null,
  editEndText: null as string | null,
  editSplitAtText: null as string | null,
  editCrossfadeSection: 'latest' as EditCrossfadeSection,
  editCrossfadeSecondsText: '2',
  editCutExecuted: false,
  editSplitParts: null as { firstSongId: string; secondSongId: string } | null,
  editCrossfadeFirstVersionId: null as string | null,
  editCrossfadeSecondVersionId: null as string | null,
  editFadeSecondsText: '2',
  uploadFile: null as File | null,
  uploadContainer: null as AudioContainer | null,
  uploadTitle: '',
  uploadArtistName: '',
  uploadGenre: '',
  uploadArtistGender: '',
  uploadUploaderName: '',
  uploadSubmitting: false,
  pendingCover: null as PendingCover | null,
};

export const useStudioStore = create<StudioState>((set, get) => ({
  ...INITIAL,
  remixLyricsOverride: INITIAL.remixLyricsOverride,

  openStudio(songId) {
    // compareIds is cleared on open. It used to persist, so version ids from a
    // previously opened song leaked into the new song's Compare panel, where they
    // resolved to nothing.
    //
    // pendingCover is cleared for the same class of reason and a worse consequence:
    // un-applied artwork carried over to the next song would offer an Apply button
    // that writes one song's cover onto another.
    //
    // readyStemsSongIds is deliberately left alone: once a song's stems exist, leaving
    // the workspace and coming back must not hide the real WAV downloads again.
    //
    // `lastSongId` is written here and NOT cleared on close: it is the answer to
    // "which track was I working on?", which the /studio entry route asks.
    set({
      songId,
      lastSongId: songId,
      resumeStudio: false,
      compareIds: [],
      pendingCover: null,
      extendFromText: INITIAL.extendFromText,
      extendSecondsText: INITIAL.extendSecondsText,
      extendTemperatureText: INITIAL.extendTemperatureText,
      extendVarianceText: INITIAL.extendVarianceText,
      replaceFrom: INITIAL.replaceFrom,
      replaceTo: INITIAL.replaceTo,
      replaceDuration: INITIAL.replaceDuration,
      replaceSteps: INITIAL.replaceSteps,
      replacePrompt: INITIAL.replacePrompt,
    });
  },

  closeStudio() {
    /*
      Leaving the workspace is not the same thing as abandoning the tool the user was in.
      The route can unmount because they clicked another nav item, and reopening Studio on
      the same song should return to that same section. `openStudio(songId)` now preserves
      `drawer` for this reason; `closeStudio()` therefore clears only what must not survive
      without an open song. The upload form values, however, stay live in the store so
      leaving Upload and returning does not erase a half-filled form.
    */
    set({
      songId: null,
      resumeStudio: Boolean(get().songId),
    });
  },

  setSongId(songId) {
    set({ songId, lastSongId: songId });
  },

  setDrawer(drawer) {
    set({ drawer });
  },

  setProcessing(processing) {
    set({ processing });
  },

  markStemsReady(songId) {
    if (!get().readyStemsSongIds.includes(songId)) {
      set({ readyStemsSongIds: [...get().readyStemsSongIds, songId] });
    }
  },

  clearReadyStems(songId) {
    set({ readyStemsSongIds: get().readyStemsSongIds.filter((id) => id !== songId) });
  },

  setPendingCover(pendingCover) {
    set({ pendingCover });
  },

  toggleCompare(versionId) {
    const prev = get().compareIds;
    if (prev.includes(versionId)) {
      set({ compareIds: prev.filter((id) => id !== versionId) });
      return;
    }
    set({ compareIds: prev.length === 1 ? [...prev, versionId] : [versionId] });
  },

  clearCompare() {
    set({ compareIds: [] });
  },

  dropFromCompare(versionId) {
    const prev = get().compareIds;
    // A pair with one member removed is no longer a valid comparison, so the
    // selection is dropped entirely rather than left half-populated — that is
    // what crashed the Compare panel on `key={v.id}` of an undefined record.
    if (!prev.includes(versionId)) return;
    set({ compareIds: [] });
  },

  setField(field, value) {
    set({ [field]: value } as Pick<StudioState, typeof field>);
  },

  setEditForm(snapshot) {
    set(snapshot);
  },

  resetEditForm() {
    set({
      editActiveSection: INITIAL.editActiveSection,
      editStartText: INITIAL.editStartText,
      editEndText: INITIAL.editEndText,
      editSplitAtText: INITIAL.editSplitAtText,
      editCrossfadeSection: INITIAL.editCrossfadeSection,
      editCrossfadeSecondsText: INITIAL.editCrossfadeSecondsText,
      editCutExecuted: INITIAL.editCutExecuted,
      editSplitParts: INITIAL.editSplitParts,
      editCrossfadeFirstVersionId: INITIAL.editCrossfadeFirstVersionId,
      editCrossfadeSecondVersionId: INITIAL.editCrossfadeSecondVersionId,
      editFadeSecondsText: INITIAL.editFadeSecondsText,
    });
  },

  setUploadForm(snapshot) {
    set(snapshot);
  },

  setUploadSubmitting(submitting) {
    set({ uploadSubmitting: submitting });
  },

  buildAction(
    kind: Exclude<
      StudioActionKind,
      'trim' | 'cut' | 'split' | 'crossfade' | 'insertSilence' | 'fade' | 'normalize' | 'master'
    >,
    lyrics
  ) {
    const s = get();
    if (!s.songId) return null;
    switch (kind) {
      case 'regenerate':
        // The edited lyric sheet IS the request. An empty one is not a regeneration
        // of anything, so it produces no action rather than a silent no-lyrics call.
        if (!lyrics || !lyrics.trim()) return null;
        return { kind, songId: s.songId, lyrics };
      case 'extend':
        return {
          kind,
          songId: s.songId,
          extendFromS: s.extendFromText ?? '',
          extendS: s.extendSecondsText,
          instruction: s.extendPrompt,
          temperature: Number.parseFloat(s.extendTemperatureText) || 1,
          classifierFreeGuidance: Number.parseFloat(s.extendVarianceText) || 3,
        };
      case 'remix':
        return {
          kind,
          songId: s.songId,
          style: s.remixStyle ?? REMIX_STYLES[0],
          prompt: s.remixPrompt,
          ...(s.remixLyricsOverride.trim() ? { lyricsOverride: s.remixLyricsOverride } : null),
        };
      case 'replace':
        return {
          kind,
          songId: s.songId,
          from: s.replaceFrom,
          to: s.replaceTo,
          duration: s.replaceDuration,
          steps: s.replaceSteps,
          prompt: s.replacePrompt,
        };
      case 'stems':
        return { kind, songId: s.songId, shifts: s.stemsShifts };
      case 'cover':
        return { kind, songId: s.songId, style: s.coverStyle, prompt: s.coverPrompt };
      default:
        return null;
    }
  },

  reset() {
    set({ ...INITIAL });
  },
}));
